/** A short-lived bearer token the app-api hands out (data plane or Mighty World). */
export interface ExpiringToken {
  token: string;
  expires_at: number;
}

/**
 * The single data-token provider. Caches the token, refreshes it `leadSeconds` before expiry (on a timer, so
 * long-lived streams and tile reads never see an expired token), and shares one in-flight request.
 */
export interface TokenProvider {
  getToken(): Promise<string>;
  dispose(): void;
}

export interface TokenProviderOptions {
  fetchToken: () => Promise<ExpiringToken>;
  leadSeconds?: number;
  /** ms since epoch; injectable for tests */
  now?: () => number;
}

/** Never refresh more often than this, whatever the server says: a bad expiry must not become a hot loop. */
export const MIN_REFRESH_MS = 10_000;

/** When the next refresh is due, in ms from `nowMs`. Never negative. */
export function refreshDelayMs(expiresAtSeconds: number, nowMs: number, leadSeconds = 60): number {
  return Math.max(0, expiresAtSeconds * 1000 - leadSeconds * 1000 - nowMs);
}

/** The expiry as epoch seconds, or an error naming the bad value (the contract is a finite number). */
export function parseExpiry(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    throw new Error(`data token expires_at must be epoch seconds, got ${JSON.stringify(value)}`);
  }
  return value;
}

export function createTokenProvider({ fetchToken, leadSeconds = 60, now = Date.now }: TokenProviderOptions) {
  let token: string | null = null;
  let expiresAt = 0;
  let inFlight: Promise<string> | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;

  const fresh = (): boolean => token !== null && refreshDelayMs(expiresAt, now(), leadSeconds) > 0;

  const schedule = (): void => {
    if (timer) clearTimeout(timer);
    if (disposed) return;
    timer = setTimeout(
      () => {
        timer = null;
        refresh().catch(() => {
          // Leave the stale token; the next getToken() retries.
          token = null;
        });
      },
      Math.max(MIN_REFRESH_MS, refreshDelayMs(expiresAt, now(), leadSeconds)),
    );
  };

  const refresh = (): Promise<string> => {
    if (inFlight) return inFlight;
    inFlight = fetchToken()
      .then((res) => {
        expiresAt = parseExpiry(res.expires_at);
        token = res.token;
        schedule();
        return res.token;
      })
      .finally(() => {
        inFlight = null;
      });
    return inFlight;
  };

  const provider: TokenProvider = {
    getToken() {
      if (fresh()) return Promise.resolve(token as string);
      return refresh();
    },
    dispose() {
      disposed = true;
      if (timer) clearTimeout(timer);
      timer = null;
    },
  };
  return provider;
}
